import { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { mode, setTheme, resolvedTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const options = [
    { value: 'light' as const, icon: Sun, label: '亮色' },
    { value: 'dark' as const, icon: Moon, label: '暗色' },
    { value: 'system' as const, icon: Monitor, label: '跟隨系統' },
  ];

  const getCurrentIcon = () => {
    if (mode === 'system') {
      return resolvedTheme === 'dark' ? Moon : Sun;
    }
    return mode === 'dark' ? Moon : Sun;
  };

  const CurrentIcon = getCurrentIcon();

  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "relative w-10 h-10 rounded-lg flex items-center justify-center",
          "transition-all duration-200",
          "hover:bg-accent/50",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "text-foreground",
          isOpen && "bg-accent/50"
        )}
        aria-label="切換主題"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <CurrentIcon className="h-5 w-5" />

        {/* Subtle glow effect */}
        <span className="absolute inset-0 rounded-lg bg-primary/10 opacity-0 hover:opacity-100 transition-opacity" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            "absolute top-full mt-2 right-0 z-50",
            "min-w-[160px] p-1.5",
            "bg-card/95 backdrop-blur-xl",
            "border border-border/50",
            "rounded-xl shadow-2xl shadow-primary/5",
            "animate-fade-in-down"
          )}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="theme-toggle-button"
        >
          {options.map((option) => {
            const Icon = option.icon;
            const isSelected = mode === option.value;
            const isSystemActive = option.value === 'system' && (
              resolvedTheme === 'dark' ? 'dark' : 'light'
            );

            return (
              <button
                key={option.value}
                onClick={() => {
                  setTheme(option.value);
                  setIsOpen(false);
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg",
                  "text-sm font-medium transition-all duration-150",
                  "hover:bg-accent/50",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isSelected ? "bg-accent text-accent-foreground" : "text-foreground/70 hover:text-foreground"
                )}
                role="menuitem"
                aria-checked={isSelected}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1 text-left">{option.label}</span>

                {/* Checkmark for selected option */}
                {isSelected && (
                  <Check className="h-4 w-4 text-primary shrink-0" />
                )}

                {/* System indicator */}
                {option.value === 'system' && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    ({isSystemActive === 'dark' ? '暗色' : '亮色'})
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Compact version for smaller spaces
export function ThemeToggleCompact({ className }: ThemeToggleProps) {
  const { mode, setTheme, resolvedTheme } = useTheme();

  const toggleTheme = () => {
    if (mode === 'light') {
      setTheme('dark');
    } else if (mode === 'dark') {
      setTheme('system');
    } else {
      setTheme('light');
    }
  };

  const getCurrentIcon = () => {
    if (mode === 'system') {
      return resolvedTheme === 'dark' ? Moon : Sun;
    }
    return mode === 'dark' ? Moon : Sun;
  };

  const CurrentIcon = getCurrentIcon();

  return (
    <button
      onClick={toggleTheme}
      className={cn(
        "relative w-9 h-9 rounded-lg flex items-center justify-center",
        "transition-all duration-200",
        "hover:bg-accent/50 hover:scale-105 active:scale-95",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "text-foreground",
        className
      )}
      aria-label="切換主題"
    >
      <CurrentIcon className="h-4 w-4" />

      {/* Mode indicator dot */}
      <div className="absolute -top-0.5 -right-0.5 flex gap-0.5">
        <span className={cn(
          "w-1 h-1 rounded-full transition-colors duration-200",
          mode === 'light' ? "bg-amber-400" : "bg-muted"
        )} />
        <span className={cn(
          "w-1 h-1 rounded-full transition-colors duration-200",
          mode === 'dark' ? "bg-indigo-400" : "bg-muted"
        )} />
      </div>

      {/* Glow effect on hover */}
      <span className="absolute inset-0 rounded-lg bg-gradient-to-r from-amber-400/20 to-indigo-400/20 opacity-0 hover:opacity-100 transition-opacity" />
    </button>
  );
}
